import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, UploadCloud, Film, Copy, Check, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { uploadMedia, formatBytes } from "@/lib/media";
import { mediaSrc } from "@/lib/img";

export default function MediaLibrary({ accept = "any", onSelect = null }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState("");
  const [optimizing, setOptimizing] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/media");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFiles = async (files) => {
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        setProgress(0);
        await uploadMedia(file, setProgress);
      }
      toast.success("Upload complete");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/media/${id}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
      toast.success("Removed from library");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const optimizeExisting = async () => {
    setOptimizing(true);
    try {
      const { data } = await api.post("/admin/media/optimize");
      toast.success(
        data.optimized
          ? `Optimised ${data.optimized} image${data.optimized > 1 ? "s" : ""} — saved ${formatBytes(data.bytes_saved)}`
          : "Everything is already optimised"
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOptimizing(false);
    }
  };

  const copy = (url) => {
    navigator.clipboard?.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(""), 1600);
  };

  const shown = accept === "any" ? items : items.filter((m) => m.kind === accept);
  const inputAccept = accept === "image" ? "image/*" : accept === "video" ? "video/*" : "image/*,video/*";

  return (
    <div data-testid="media-library">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold">Media Library</p>
          <p className="text-xs text-muted-foreground">
            Images and video up to 200 MB. Photos are auto-compressed to WebP (max 2560px) on upload.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="media-optimize-btn"
            onClick={optimizeExisting}
            disabled={optimizing}
            className="btn-outline h-10 px-4 text-xs disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4" />
            {optimizing ? "Optimising…" : "Optimise existing"}
          </button>
          <label className="btn-gold h-10 cursor-pointer px-5 text-xs">
            <UploadCloud className="h-4 w-4" />
            {busy ? `Uploading ${progress}%` : "Upload files"}
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={inputAccept}
              className="hidden"
              disabled={busy}
              data-testid="media-upload-input"
              onChange={(e) => e.target.files?.length && handleFiles(e.target.files)}
            />
          </label>
        </div>
      </div>

      {busy && (
        <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-border" data-testid="media-upload-progress">
          <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}

      {shown.length === 0 ? (
        <p className="py-14 text-center text-sm text-muted-foreground" data-testid="media-empty">
          Nothing here yet. Upload an image or video to get started.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-grid">
          {shown.map((m) => (
            <div
              key={m.id}
              data-testid={`media-item-${m.id}`}
              className="group overflow-hidden border border-border bg-card/40"
              style={{ borderRadius: "var(--radius)" }}
            >
              <button
                type="button"
                data-testid={`media-select-${m.id}`}
                onClick={() => onSelect && onSelect(m)}
                disabled={!onSelect}
                className="relative block h-28 w-full bg-muted"
              >
                {m.kind === "image" ? (
                  <img src={mediaSrc(m.url, 320)} alt={m.filename} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <Film className="h-7 w-7" />
                  </span>
                )}
                {onSelect && (
                  <span className="absolute inset-0 flex items-center justify-center bg-primary/85 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground opacity-0 transition-opacity group-hover:opacity-100">
                    Use this
                  </span>
                )}
              </button>
              <div className="p-3">
                <p className="truncate text-xs" title={m.filename}>{m.filename}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {m.kind} · {formatBytes(m.size)}
                  {m.original_size && m.original_size > m.size
                    ? ` · from ${formatBytes(m.original_size)}`
                    : ""}
                  {m.width ? ` · ${m.width}×${m.height}` : ""}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    data-testid={`media-copy-${m.id}`}
                    onClick={() => copy(m.url)}
                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                  >
                    {copied === m.url ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copied === m.url ? "Copied" : "Copy URL"}
                  </button>
                  <button
                    type="button"
                    data-testid={`media-delete-${m.id}`}
                    onClick={() => remove(m.id)}
                    className="inline-flex items-center gap-1 text-[11px] text-destructive hover:underline"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
